/**
 * Command palette (⌘K / Ctrl+K) — keyboard-first navigation.
 *
 * Inspired by the Datadog / Linear / Palantir command surfaces:
 * a single overlay that lists every command with fuzzy matching, jumps
 * to the most relevant action by Enter, supports arrow navigation and
 * is dismissable with Esc / overlay click.
 *
 * Keep it dependency-free (no cmdk import) so the bundle stays small.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icon, type IconName } from "./Icon";
import { useTheme } from "../system/ThemeContext";
import { useCopilot } from "../system/CopilotContext";

interface CommandItem {
  id: string;
  section: string;
  title: string;
  hint?: string;
  icon: IconName;
  to?: string;
  action?: () => void;
}

function fuzzyMatch(query: string, text: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let i = 0;
  for (const c of t) {
    if (c === q[i]) i += 1;
    if (i === q.length) return true;
  }
  return t.includes(q);
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps): JSX.Element | null {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { openCopilot } = useCopilot();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  const commands = useMemo<CommandItem[]>(
    () => [
      // Navegación principal
      { id: "nav.home", section: "Ir a", title: "Inicio · Centro Ejecutivo", icon: "home", to: "/" },
      { id: "nav.alerts", section: "Ir a", title: "Centro de Alertas", icon: "bell", to: "/alertas" },
      { id: "nav.cases", section: "Ir a", title: "Casos Prioritarios", icon: "flask", to: "/casos" },
      { id: "nav.insights", section: "Ir a", title: "Operational Insights", icon: "search", to: "/insights" },
      { id: "nav.live", section: "Ir a", title: "Live Operations", icon: "wifi", to: "/live" },
      { id: "nav.map", section: "Ir a", title: "Mapa Operacional", icon: "search", to: "/mapa" },
      { id: "nav.dashboards", section: "Ir a", title: "Dashboards de consumo", icon: "dashboard", to: "/operador/dashboards" },
      { id: "nav.balance", section: "Ir a", title: "Balance & NRW", icon: "droplet", to: "/analista/balance" },
      { id: "nav.impacto", section: "Ir a", title: "Impacto Económico", icon: "report", to: "/impacto" },
      { id: "nav.prediccion", section: "Ir a", title: "Predicción & ML", icon: "dashboard", to: "/prediccion" },
      { id: "nav.whatif", section: "Ir a", title: "Simulaciones what-if", icon: "flask", to: "/analista/whatif" },
      { id: "nav.reports", section: "Ir a", title: "Reportes", icon: "report", to: "/analista/reportes" },
      { id: "nav.orders", section: "Ir a", title: "Órdenes de inspección", icon: "wrench", to: "/inspector/ordenes" },
      { id: "nav.crews", section: "Ir a", title: "Cuadrillas", icon: "users", to: "/cuadrillas" },
      { id: "nav.obs", section: "Ir a", title: "Observabilidad", icon: "wifi", to: "/observabilidad" },
      { id: "nav.lora", section: "Ir a", title: "LoRa Network Intelligence", icon: "wifi", to: "/lora" },
      { id: "nav.dq", section: "Ir a", title: "Calidad de datos", icon: "shield", to: "/calidad" },
      { id: "nav.audit", section: "Ir a", title: "Auditoría", icon: "shield", to: "/supervisor/auditoria" },
      { id: "nav.params", section: "Ir a", title: "Parámetros", icon: "settings", to: "/supervisor/parametros" },
      { id: "nav.cat", section: "Ir a", title: "Catastro", icon: "users", to: "/supervisor/catastro" },

      // Copilot
      {
        id: "copilot.open",
        section: "ANC Copilot",
        title: "Abrir ANC Copilot",
        icon: "flask",
        action: () => openCopilot(),
        hint: "IA",
      },
      {
        id: "copilot.nrw",
        section: "ANC Copilot",
        title: "¿Por qué aumentó el NRW en DMA-003?",
        icon: "flask",
        action: () => openCopilot("¿Por qué aumentó el NRW en DMA-003?"),
      },
      {
        id: "copilot.roi",
        section: "ANC Copilot",
        title: "¿Qué casos tienen mayor ROI?",
        icon: "flask",
        action: () => openCopilot("¿Qué casos tienen mayor ROI?"),
      },
      {
        id: "copilot.alerts",
        section: "ANC Copilot",
        title: "Resume las alertas críticas",
        icon: "flask",
        action: () => openCopilot("Resume las alertas críticas."),
      },

      // Comandos rápidos
      {
        id: "cmd.theme",
        section: "Acciones",
        title: theme === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro",
        icon: "settings",
        action: toggleTheme,
        hint: "Theme",
      },
      {
        id: "cmd.refresh",
        section: "Acciones",
        title: "Recargar datos",
        icon: "clock",
        action: () => window.location.reload(),
      },
    ],
    [theme, toggleTheme, openCopilot],
  );

  const filtered = useMemo(
    () => commands.filter((c) => fuzzyMatch(query, `${c.section} ${c.title}`)),
    [commands, query],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(filtered.length - 1, a + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      } else if (e.key === "Enter") {
        const target = filtered[active];
        if (!target) return;
        e.preventDefault();
        if (target.to) navigate(target.to);
        else if (target.action) target.action();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose, filtered, active, navigate]);

  if (!open) return null;

  // Group by section preserving filtered order
  const sections = new Map<string, CommandItem[]>();
  for (const cmd of filtered) {
    const list = sections.get(cmd.section) ?? [];
    list.push(cmd);
    sections.set(cmd.section, list);
  }

  return (
    <div
      className="cmd-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="cmd-panel" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="search"
          className="cmd-input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          placeholder="Buscar páginas, comandos, alertas…"
          aria-label="Buscar"
        />
        <div className="cmd-list">
          {filtered.length === 0 ? (
            <div className="cmd-empty">Sin coincidencias</div>
          ) : (
            Array.from(sections.entries()).map(([section, items]) => (
              <div key={section}>
                <div className="cmd-section">{section}</div>
                {items.map((cmd) => {
                  const idx = filtered.indexOf(cmd);
                  return (
                    <div
                      key={cmd.id}
                      className="cmd-item"
                      data-active={idx === active}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => {
                        if (cmd.to) navigate(cmd.to);
                        else if (cmd.action) cmd.action();
                        onClose();
                      }}
                    >
                      <Icon name={cmd.icon} size={14} />
                      <span>{cmd.title}</span>
                      {cmd.hint && <span className="cmd-item__hint">{cmd.hint}</span>}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
